import _ from 'lodash';

import React, { Fragment, useMemo, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Stack, Typography, useTheme } from '@mui/material';

import { Page } from '../theme/elements/Page';
import { FormCard, FormCardLayout } from 'theme/elements/Forms';
import { FormButton } from 'theme/elements/Buttons';

import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';

import { selectTranslations } from 'state/reducers/translation.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';
import { getAltshulerAccountList } from 'state/reducers/global.reducer';

import { approveFeature, getFeature, declineFeature } from 'services/pending-requests.service';

import FormCreatorComponent, { isValidForm } from 'components/FormCreatorComponent';
import { fieldType, fieldMode } from 'components/FormCreatorComponent/types';
import { DialogBox } from 'components/DialogBox';
import TextArea from 'components/FormCreatorComponent/inputs/TextArea';
import CheckBox from 'components/FormCreatorComponent/inputs/CheckBox';
import { PopupComponent } from 'components/PopupComponent';
import { ButtonComponent } from 'components/ButtonComponent';

import { pendingRequestsTypesForms } from 'sections/pendingRequests/forms.types';
import {
	getCompanyFeatureProfileApprovedPayload,
	getCompanyFeatureProfileDeclinedPayload,
	getFeaturePayload,
	getFormsValuesByType,
	getInitialProfileFormValues,
	isFormNeedsHidingInProfile,
	getOptionsDependenciesForProfile,
	getCustomDependenciesForProfile,
	isAllowSubmitForProfile,
	getFieldCustomizationsForProfile,
} from 'sections/pendingRequests/helper';
import { overrideFormsToProfile } from 'sections/pendingRequests/forms.types.profile';

import {
	mapingFeature,
	mapingFeatureToEndpoint,
	mappingTypeForProfile,
	permissionsForProfile,
} from 'constants/pending-requests.constants';
import { pagesEnum } from 'constants/ui.constants';
import { setSuccessMessage } from 'state/reducers/ui.reducers';
import { clearBoSignerRequests } from 'state/reducers/pending-requests.reducer';
import { pendingRequestsTypes, ProfileFormsTypesNeedDialog } from 'constants/requests.constants';
import { BalanceType, depositType, BalanceStatus } from 'constants/balance.constants';

// Interface for generic popup configuration
interface IPopupConfig {
	title: string;
	subtitle: string;
	boldSubtitle?: string;
	onConfirm: () => void;
}

export const CompanyFeatureProfileApp = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();
	const theme = useTheme();
	const basicButtonStyle = { borderRadius: '8px', width: '320px', height: '48px', marginTop: '10px' };
	const [item, setItem] = useState<Record<string, any>>();
	const [formState, setFormState] = useState<Record<string, any>>({});
	const [BoNotes, setBoNotes] = useState<string | null>(null);
	const { type, id, subType } = useParams();
	const { permissions } = useAppSelector(getUserDetails);
	const altshulerAccounts = useAppSelector(getAltshulerAccountList);
	const [customDependencies, setCustomDependencies] = useState<Record<string, any>>({});
	const [openDialog, setOpenDialog] = useState(false);
	const [isUserApprovedRequest, setIsUserApprovedRequest] = useState(false);
	const [isFinalApprove, setIsFinalApprove] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [popupConfig, setPopupConfig] = useState<IPopupConfig | null>(null);
	const formCurrentValues = useRef<Record<string, any>>({});

	const hasSubmitPermission: boolean = useMemo(() => {
		if (!permissions || !item) {
			return false;
		}
		if (!isAllowSubmitForProfile(type as pendingRequestsTypes, item)) {
			return false;
		}
		if (permissions.permissions) {
			const requestedPermissions = (permissionsForProfile as any)[type as pendingRequestsTypes] || [];
			return _.difference(requestedPermissions, permissions.permissions).length !== requestedPermissions.length;
		}
		return false;
	}, [permissions, item]);

	const formValues = useMemo(() => {
		const _type = _.has(mappingTypeForProfile, type || '') ? _.get(mappingTypeForProfile, type || '') : type;
		const props = getFeaturePayload(type || '', id || '');
		if (!item) {
			if (props.name !== '') {
				dispatch(getFeature(props))
					.unwrap()
					.then((originalPromiseResult) => {
						const _item = _.get(originalPromiseResult, _.get(mapingFeature, _type || '') as string);
						if (_item) {
							let additionalProps = {};
							if (!_.isNil(originalPromiseResult?.supportRequest)) {
								additionalProps = {
									message: originalPromiseResult?.supportRequest?.message,
									companyBankAccountId: _item?.bankAccountId,
									bankName: !_.isNil(_.get(_item, 'bankAccount'))
										? `${_.get(_item, 'bankAccount.bankName.bankName')}  ${_.get(
												_item,
												'bankAccount.accountNumber'
										  )}/${_.get(_item, 'bankAccount.bankBranch')}`
										: '',
								};
							} else {
								additionalProps = {
									message: _item?.boNotes,
								};
							}
							setItem({ ..._item, ...getInitialProfileFormValues(_item, _type!), ...additionalProps });
						}
					})
					.catch((rejectedValueOrSerializedError) => {
						// handle error here
						console.log(rejectedValueOrSerializedError);
					});
			}
		} else {
			try {
				if (item) {
					const pickFormValues = getFormsValuesByType(item, _type);
					return pickFormValues;
				}
			} catch (e) {
				console.log(e);
				return undefined;
			}
		}
		return undefined;
	}, [item, subType]);

	const optionsDependencies = getOptionsDependenciesForProfile(type as pendingRequestsTypes, item);

	const forms = useMemo(() => {
		const _type = _.has(mappingTypeForProfile, type || '') ? _.get(mappingTypeForProfile, type || '') : type;
		if (formValues && type && _.has(pendingRequestsTypesForms, _type)) {
			const formType =
				item?.type === BalanceType.Withdrawal
					? (pendingRequestsTypes.WITHDRAWAL_SUPPORT as string)
					: (_type as string);
			const basicForms = _.get(pendingRequestsTypesForms, formType);
			const override = _.get(overrideFormsToProfile, formType);
			const _forms = _.merge({}, basicForms, override);
			const formsToShow = _.filter(_forms, (form) => !form.hide && !isFormNeedsHidingInProfile(form, type, item));
			const k = _.map(formsToShow, (form) => ({
				...form,
				fieldsGroups: _.map(form.fieldsGroups, (group) => ({
					...group,
					fields: _.map(group.fields, (field) => {
						// customizations for the fields properties like mode and disabled
						const customizations = getFieldCustomizationsForProfile(
							field,
							_type,
							item,
							hasSubmitPermission
						);

						return {
							...field,
							mode: customizations.mode ?? field.mode,
							props: {
								...field.props,
								disabled: customizations.disabled ?? field.props?.disabled,
								options:
									_.isEqual(field.type, fieldType.select) &&
									_.has(optionsDependencies, field.props.name)
										? _.get(optionsDependencies, field.props.name)
										: field.props?.options || [],
								customData:
									_.isEqual(field.type, fieldType.custom) &&
									_.has(customDependencies, field.props.name)
										? _.get(customDependencies, field.props.name)
										: field.props?.customData || [],
							},
						};
					}),
				})),
			}));
			return k;
		}
		return undefined;
	}, [formValues, optionsDependencies, customDependencies, item]);

	useEffect(() => {
		if (formValues && forms) {
			let formsState: Record<string, any> = {};
			forms.forEach((form) => {
				const dirtyValues =
					_.has(formState, form.name) && _.has(formState[form.name], 'dirtyFields')
						? _.get(formState[form.name], 'dirtyFields')
						: {};
				formsState = {
					...formsState,
					[form.name]: isValidForm(form.fieldsGroups, { ...formValues, ...dirtyValues }, true),
				};
			});
			setFormState(formsState);
		}
	}, [formValues]);
	useEffect(() => {
		if (
			altshulerAccounts &&
			item &&
			[pendingRequestsTypes.BALANCES, pendingRequestsTypes.BALANCES_BO_REQUESTS].includes(
				type as pendingRequestsTypes
			)
		) {
			const account = altshulerAccounts?.find((altAccount: any) => altAccount.id === item?.altshulerAccountId);
			if (!account) {
				return;
			}
			setCustomDependencies(getCustomDependenciesForProfile(type as pendingRequestsTypes, item, account));
		}
	}, [altshulerAccounts, item]);

	useEffect(() => {
		if (permissions.permissions) {
			const permission = 'read_' + _.get(mapingFeatureToEndpoint, type || '');
			if (!permissions.permissions.includes(permission)) {
				navigate('/' + pagesEnum.PAGE_NOT_FOUND);
			}
		}
	}, [permissions]);

	const handleApprove = () => {
		if (id && type) {
			const props = getCompanyFeatureProfileApprovedPayload('', type, id, BoNotes || '', {
				...formValues,
				...formCurrentValues.current,
			});

			if (props.name !== '') {
				setIsSaving(true);
				dispatch(approveFeature(props))
					.unwrap()
					.then((originalPromiseResult) => {
						setIsSaving(false);
						if (originalPromiseResult?.success) {
							dispatch(setSuccessMessage(true));
						}
						if (type === pendingRequestsTypes.BALANCES) {
							dispatch(clearBoSignerRequests());
							navigate(`/${pagesEnum.DASHBOARD}/${pagesEnum.CLIENTS_DASHBOARD_SIGNER}`);
						} else {
							navigate(`/${pagesEnum.DASHBOARD}`);
						}
					})
					.catch((rejectedValueOrSerializedError) => {
						// handle error here
						setIsSaving(false);
						console.log(rejectedValueOrSerializedError);
					});
			}
		}
	};

	const handleDecline = () => {
		if (id && type) {
			let values = formValues;
			_.values(formState).forEach(
				(prop) =>
					(values =
						prop?.dirtyFields && !_.isEmpty(prop.dirtyFields) ? { ...values, ...prop.dirtyFields } : values)
			);

			const props = getCompanyFeatureProfileDeclinedPayload('', type, id, BoNotes || '', {
				...formValues,
				...values,
			});
			if (props.name !== '') {
				setIsSaving(true);
				dispatch(declineFeature(props))
					.unwrap()
					.then((originalPromiseResult) => {
						setIsSaving(false);
						if (originalPromiseResult?.success) {
							dispatch(setSuccessMessage(true));
						}
						navigate(-1);
					})
					.catch((rejectedValueOrSerializedError) => {
						// handle error here
						setIsSaving(false);
						console.log(rejectedValueOrSerializedError);
					});
			}
		}
	};

	const getIsUserApproved = () => {
		switch (type) {
			case pendingRequestsTypes.BALANCES:
				if (item?.depositType === depositType.Digital && item?.status === BalanceStatus.Pending) {
					const selectedDigitalStatusOption =
						formState['page.pendingRequests.balance.field.transferDetails']?.dirtyFields?.status;
					return selectedDigitalStatusOption !== BalanceStatus.Pending;
				}
				const selectedApproveOption =
					formState['page.pendingRequests.balance.field.bo.confirm.mainTitle']?.dirtyFields?.selectApprove;
				return selectedApproveOption === 'Approve';
			default:
				return true;
		}
	};

	const handlePopupConfirm = () => {
		popupConfig?.onConfirm();
		setPopupConfig(null);
	};

	const handlePopupCancel = () => {
		setPopupConfig(null);
	};

	const handleConfirm = () => {
		const requestApproved = getIsUserApproved();
		setIsUserApprovedRequest(requestApproved);

		// Check if this is a digital balance approval request that needs confirmation
		if (requestApproved && type === pendingRequestsTypes.BALANCES && item?.depositType === depositType.Digital) {
			const baseKey = 'page.profile.forms.digital_balance.status_change';
			setPopupConfig({
				title: t(`${baseKey}.title`),
				subtitle: t(`${baseKey}.${_.camelCase(formCurrentValues.current.status)}.subtitle`),
				onConfirm: handleApprove,
			});

			return;
		}

		if (requestApproved && !ProfileFormsTypesNeedDialog.includes(type as pendingRequestsTypes)) {
			handleApprove();
			return;
		}

		setOpenDialog(true);
	};

	const applyChanges = (source: Record<string, any>, changes: Record<string, any>) => {
		const result = { ...source };

		for (const [key, value] of Object.entries(changes)) {
			if (value === undefined || value === null) {
				delete result[key]; // remove key if null or undefined
			} else {
				result[key] = value; // otherwise update
			}
		}

		return result;
	};

	const updateFormCurrentValues = (form: any) => {
		const actualChanges: Record<string, any> = {};
		const fieldsToRemove: Record<string, any> = {};

		// Check each dirty field to see if it's actually different from the original
		_.forEach(form.dirtyFields, (value, key) => {
			const originalValue = _.get(item, key);
			if (!_.isEqual(value, originalValue)) {
				// Field has actually changed - keep it
				actualChanges[key] = value;
			} else {
				// Field is back to original value - mark for removal
				fieldsToRemove[key] = undefined;
			}
		});

		// Apply actual changes and remove fields that are back to original values
		formCurrentValues.current = applyChanges(formCurrentValues.current, {
			...actualChanges,
			...fieldsToRemove,
		});
	};

	const isConfirmDisabled = useMemo(() => {
		if (_.values(formState)?.length && item) {
			_.forEach(_.values(formState), (form) => {
				if (form.isDirty) {
					updateFormCurrentValues(form);
				}
			});

			return !_.every(_.values(formState), { isValid: true }) || _.isEqual(formCurrentValues.current, {});
		}
		return true;
	}, [item, formState]);

	const pageTitle = useMemo(() => {
		if (!item || !type) {
			return null;
		}

		if (
			[pendingRequestsTypes.BALANCES, pendingRequestsTypes.BALANCES_BO_REQUESTS].includes(
				type as pendingRequestsTypes
			)
		) {
			if (item?.depositType === depositType.Digital) {
				return t('page.profile.balancesRequests.digital.title');
			}
		}

		let titleKey = type;
		if (
			item?.type === BalanceType.Withdrawal &&
			[pendingRequestsTypes.BALANCES, pendingRequestsTypes.BALANCES_BO_REQUESTS].includes(
				type as pendingRequestsTypes
			)
		) {
			titleKey = _.camelCase(pendingRequestsTypes.WITHDRAWAL_SUPPORT);
		}

		return t('page.profile.' + titleKey + '.title');
	}, [item, type, t]);

	return (
		<Fragment>
			<Page includeGoBack title={pageTitle} loading={isSaving}>
				<FormCardLayout>
					<>
						{forms &&
							forms.map((form, index: number) => {
								return (
									<FormCard key={index} title={t(form.name)}>
										<FormCreatorComponent
											{...form}
											translation={t}
											values={formValues}
											checkInitValidation={false}
											handleChange={(newFormState: Record<string, any>) => {
												setFormState({ ...formState, [form.name]: newFormState });
											}}
										/>
									</FormCard>
								);
							})}
					</>
					<Stack direction="row" justifyContent="space-between" width="100%">
						<FormButton>
							<Button
								onClick={() => {
									navigate(-1);
								}}
								variant="outlined"
							>
								{t('button.back')}
							</Button>
						</FormButton>
						{hasSubmitPermission && (
							<FormButton>
								<Stack direction="row" gap={2}>
									<Button
										variant="contained"
										onClick={() => {
											handleConfirm();
										}}
										disabled={isConfirmDisabled}
									>
										{t('button.ok')}
									</Button>
								</Stack>
							</FormButton>
						)}
					</Stack>
				</FormCardLayout>
			</Page>
			{popupConfig && (
				<PopupComponent
					open={!!popupConfig}
					title={popupConfig.title}
					subtitle={popupConfig.subtitle}
					isDefaultButton={false}
				>
					{popupConfig.boldSubtitle && (
						<Typography
							style={{ direction: 'rtl' }}
							variant="h4"
							sx={{ fontWeight: 700, marginBottom: '20px' }}
						>
							{popupConfig.boldSubtitle}
						</Typography>
					)}
					<ButtonComponent
						variantType={'contained'}
						handleClick={handlePopupConfirm}
						text={t('page.customerAccounts.forms.balance_refund.create_refund_popup_approve')}
						styleObj={{ ...basicButtonStyle }}
					/>
					<ButtonComponent
						variantType={'text'}
						disabled={false}
						handleClick={handlePopupCancel}
						text={`${t('page.customerAccounts.forms.balance_refund.create_refund_popup_deny')}`}
						styleObj={{
							...basicButtonStyle,
							'&:hover': {
								background: theme.palette.root.basicDeeoGreen,
								boxShadow: 'none',
								color: 'white',
							},
						}}
					/>
				</PopupComponent>
			)}
			<DialogBox
				openDialog={openDialog}
				handleClose={() => setOpenDialog(false)}
				title={isUserApprovedRequest ? t('label.approveQuestionTitle') : t('label.rejectQuestionTitle')}
				approveDisabled={isUserApprovedRequest && !isFinalApprove}
				handleApprove={() => {
					if (_.isEmpty(BoNotes) && !isUserApprovedRequest) {
						setBoNotes('');
						return false;
					}
					if (isUserApprovedRequest && !isFinalApprove) {
						return false;
					}
					isUserApprovedRequest ? handleApprove() : handleDecline();
					setOpenDialog(false);
				}}
				handleReject={() => {
					setOpenDialog(false);
				}}
			>
				<Fragment>
					{!isUserApprovedRequest && (
						<Stack>
							<Typography>{t('label.rejectQuestionSubtitle')}</Typography>
							<TextArea
								valid={_.isNull(BoNotes) || BoNotes !== ''}
								cField={{
									type: fieldType.text,
									mode: fieldMode.readWithEnableWrite,
									props: {
										name: 'bo_notes',
										label: '',
										required: true,
										value: BoNotes === null ? '' : BoNotes,
									},
									validation: [{ requierd: true }],
								}}
								handleChange={(e: any) => {
									setBoNotes(e.target.value);
								}}
								initialValidation={[]}
							></TextArea>
						</Stack>
					)}
					{isUserApprovedRequest && (
						<Stack>
							<CheckBox
								valid={_.isNull(BoNotes) || BoNotes !== ''}
								cField={{
									type: fieldType.checkbox,
									mode: fieldMode.readWithEnableWrite,
									props: {
										name: 'approveRequest',
										label: t('label.approveQuestionSubtitle'),
										required: true,
										value: 'false',
									},
									validation: [{ checked: true }],
								}}
								handleChange={(e: any) => {
									setIsFinalApprove(e.target.checked);
								}}
								initialValidation={[]}
							></CheckBox>
						</Stack>
					)}
				</Fragment>
			</DialogBox>
		</Fragment>
	);
};
